import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { useAccount as useWagmiAccount, useDisconnect, useSwitchChain } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import Navbar from "@/components/Navbar";
import CombinedTokenCards from "@/components/CombinedTokenCards";
import ActionBar from "@/components/ActionBar";
import USDBalance from "@/components/USDBalance";
import Wallet from "@/components/Wallet";
import SendModal from "@/components/modals/SendModal";
import ReceiveModal from "@/components/modals/ReceiveModal";
import Tagline from "@/components/Tagline";
import MultipleWalletsModal from "@/components/modals/MultipleWalletsModal";
import AmountInputModal from "@/components/modals/AmountInputModal";
import Footer from "@/components/Footer";
import WelcomeScreen from "@/components/WelcomeScreen";
import NetworkSelector, { COTI_TESTNET_HEX, COTI_MAINNET_HEX } from "@/components/NetworkSelector";
import { useCotiPluginContext } from "@/context/PrivacyBridgeContext";
import { useInitialChainSelector } from "@/providers/initialChainContext";
import { getPrivateTokensForChain } from "@/contracts/config";
import { useMaxBalance } from "@/hooks/useMaxBalance";
import { useCotiUnlock, usePrivateUnlock } from "@coti-io/coti-wallet-plugin";
import { useBridgeData } from "@/vendor/coti-wallet-plugin";
import { getChainConfig, getExplorerBaseUrlForChain, resolveIndexPageUi } from "@/chains";
import { resolveTokenIcon } from "@/lib/tokenIcons";
import { hasPersistedWagmiSession, isWalletSessionPending, readPendingChainSwitch } from "@/lib/wagmiSession";
import type { SendTransferParams } from "@/components/modals/SendModal";

const SESSION_RESTORE_TIMEOUT_MS = 10_000;

const Index = () => {
  const {
    // Global State
    publicTokens,
    privateTokens,
    isBridgingLoading,
    handleSwap,
    isConnected,
    walletAddress,
    handleConnect,

    // Selections from context
    amount,
    setAmount,
    setDirection: setContextDirection,
    setSelectedTokenIndex,
    direction: contextDirection,

    // Approval
    isApprovalNeeded,
    isApproving,
    handleApprove,
    lockPrivateBalances,
    chainId,
    isPrivateUnlocked,
    estimatedGasFee,
    updateGasFee,
    isGasEstimating,
    portalFeeCoti,
    portalFee,
    portalFeeSymbol,
    podInboxFee,
    l1GasFee,
    isPodChain,
    feeDebugInfo,
    podRequests,
    showMultipleWalletsModal,
    setShowMultipleWalletsModal,
    switchNetwork,
  } = useCotiPluginContext();

  const {
    isConnected: wagmiConnected,
    connector: wagmiConnector,
    chainId: wagmiChainId,
    status: wagmiStatus,
  } = useWagmiAccount();
  const { disconnectAsync } = useDisconnect();
  // Snapshot at first paint: a wiped localStorage must not keep the restore spinner up.
  const [hadPersistedSession] = useState(hasPersistedWagmiSession);
  const [connectInitiated, setConnectInitiated] = useState(false);
  const [restoreTimedOut, setRestoreTimedOut] = useState(false);
  const isWagmiReconnecting = isWalletSessionPending({
    hadPersistedSession,
    restoreTimedOut,
    connectInitiated,
    wagmiStatus,
  });

  // Plugin private unlock provider — owns AES unlock, restore, onboarding modal state.
  const privateUnlock = usePrivateUnlock();

  // useCotiUnlock for actual private operations (send, etc.)
  const { sendPrivateToken } = useCotiUnlock();

  const [selectedPublicToken, setSelectedPublicToken] = useState<string | null>(null);
  const [selectedPrivateToken, setSelectedPrivateToken] = useState<string | null>(null);

  // Controls for Modals
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showActionBarPortalModal, setShowActionBarPortalModal] = useState(false);
  const [actionBarPortalDirection, setActionBarPortalDirection] = useState<"in" | "out">("in");

  // Poll Gas Fee when AmountInputModal is open
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if ((showAmountModal || showActionBarPortalModal) && updateGasFee) {
      // Initial fetch
      updateGasFee();

      // Poll every 10 seconds (aligned with PoD status polling)
      intervalId = setInterval(() => {
        updateGasFee();
      }, 10_000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showAmountModal, showActionBarPortalModal, updateGasFee, selectedPublicToken, selectedPrivateToken]);

  const [localDirection, setLocalDirection] = useState<"to-private" | "to-public">("to-private");

  const { getSelectedTokenBalance } = useMaxBalance({
    publicTokens,
    privateTokens,
    selectedPublicToken,
    selectedPrivateToken,
    localDirection,
    estimatedGasFee,
  });

  const networkChainId = chainId ? parseInt(chainId) : 7082400;
  const indexPageUi = resolveIndexPageUi(networkChainId);
  const effectiveIsPodChain =
    isPodChain ?? getChainConfig(networkChainId)?.portalStrategy === "pod-privacy-portal";
  const effectivePortalFeeSymbol =
    effectiveIsPodChain
      ? indexPageUi.amountModalGasSymbol
      : portalFeeSymbol;
  const podFeesMatchActiveChain =
    !effectiveIsPodChain || portalFeeSymbol === indexPageUi.amountModalGasSymbol;

  // Debug: log connector info to help diagnose detection issues
  useEffect(() => {
    if (wagmiConnected && wagmiConnector) {
      console.log('🔑 [Unlock] Wagmi connector info:', {
        id: wagmiConnector.id,
        name: wagmiConnector.name,
        type: wagmiConnector.type,
      });
    }
  }, [wagmiConnected, wagmiConnector]);

  // Detect mobile device — use screen size + pointer heuristic to avoid false positives
  const isMobile = useMemo(() => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const hasTouchScreen = navigator.maxTouchPoints > 1;
    const isNarrowScreen = window.screen.width <= 768;
    const uaMatch = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return uaMatch && (hasTouchScreen || isNarrowScreen);
  }, []);

  const { bridgesData } = useBridgeData(networkChainId);

  const getPublicSymbolForPrivateToken = (symbol: string) =>
    symbol.startsWith("p.") ? symbol.replace("p.", "") : symbol.startsWith("p") ? symbol.slice(1) : symbol;

  const activePublicSymbol = selectedPublicToken ?? (selectedPrivateToken ? getPublicSymbolForPrivateToken(selectedPrivateToken) : null);
  const selectedBridge = bridgesData.find(b => b.publicToken === activePublicSymbol);
  const isBridgePaused = selectedBridge?.isPaused ?? false;
  const isDepositEnabled = selectedBridge?.isDepositEnabled ?? true;

  /* -------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------- */

  // Close private-action modals whenever balances are locked.
  // Receive is exempt — it only shows the public address.
  // Also clear token selection: locking closes the modal without onClose(),
  // which would otherwise leave a card highlighted and make the next click a no-op deselect.
  useEffect(() => {
    if (!isPrivateUnlocked) {
      setShowSendModal(false);
      setShowAmountModal(false);
      setShowActionBarPortalModal(false);
      setSelectedPublicToken(null);
      setSelectedPrivateToken(null);
      setAmount("");
    }
  }, [isPrivateUnlocked]);

  const selectPublicTokenImplementation = (symbol: string) => {
    setSelectedPublicToken(symbol);
    setSelectedPrivateToken(null);
    setLocalDirection("to-private");
    setAmount("");
    const index = publicTokens.findIndex(t => t.symbol === symbol);
    if (index !== -1) setSelectedTokenIndex(index);
    setContextDirection("to-private");
    setShowAmountModal(true);
  };

  const handleSelectPublicToken = (symbol: string) => {
    if (!isConnected) {
      handleConnect();
      return;
    }
    void privateUnlock.requireUnlock(() => selectPublicTokenImplementation(symbol));
  };

  const selectPrivateTokenImplementation = (symbol: string) => {
    setSelectedPrivateToken(symbol);
    setSelectedPublicToken(null);
    setLocalDirection("to-public");
    setAmount("");
    const publicSymbol = getPublicSymbolForPrivateToken(symbol);
    const index = publicTokens.findIndex(t => t.symbol === publicSymbol);
    if (index !== -1) setSelectedTokenIndex(index);
    setContextDirection("to-public");
    setShowAmountModal(true);
  };

  const handleSelectPrivateToken = (symbol: string) => {
    if (!isConnected) {
      handleConnect();
      return;
    }
    void privateUnlock.requireUnlock(() => selectPrivateTokenImplementation(symbol));
  };

  const handleCloseAmountModal = () => {
    setShowAmountModal(false);
    setSelectedPublicToken(null);
    setSelectedPrivateToken(null);
    setAmount("");
  };

  // Portal In: swap runs inside AmountInputModal's step-flow view.
  // overrideAmount is the grossed-up send value for COTI (desired receive + portal fee).
  const handleConfirmPortalIn = async (
    onProgress?: (stage: "approve-start" | "approve-complete" | "transfer-start" | "transfer-complete", txHash?: string) => void,
    overrideAmount?: string,
  ) => {
    await handleSwap(overrideAmount ?? amount, "to-private", undefined, onProgress);
  };

  // Portal Out: use hook progress events to drive Approve -> Transfer -> Complete in AmountInputModal.
  const handleConfirmPortalOut = async (
    onProgress?: (stage: "approve-start" | "approve-complete" | "transfer-start" | "transfer-complete", txHash?: string) => void,
    overrideAmount?: string,
  ) => {
    await handleSwap(overrideAmount ?? amount, "to-public", undefined, onProgress);
  };

  const handleSendPrivateToken = useCallback(async ({ symbol, recipient, amount: sendAmount }: SendTransferParams) => {
    if (!walletAddress || !chainId) {
      throw new Error("Wallet not connected");
    }

    // Balance refresh runs in the background inside sendPrivateToken so the
    // success screen is not blocked on private balance decryption.
    return sendPrivateToken({
      symbol,
      recipient,
      amount: sendAmount,
    });
  }, [walletAddress, chainId, sendPrivateToken]);

  const prepareActionBarPortalModal = useCallback((direction: "in" | "out") => {
    setActionBarPortalDirection(direction);
    setContextDirection(direction === "in" ? "to-private" : "to-public");
    if (direction === "in") {
      if (publicTokens.length > 0 && !selectedPublicToken) {
        setSelectedPublicToken(publicTokens[0].symbol);
        setSelectedPrivateToken(null);
        setSelectedTokenIndex(0);
      }
      return;
    }
    if (publicTokens.length > 0 && !selectedPrivateToken) {
      const priv = privateTokens.find(
        pt => pt.symbol === `p.${publicTokens[0].symbol}` || pt.symbol === `p${publicTokens[0].symbol}`,
      );
      setSelectedPrivateToken(priv?.symbol || `p.${publicTokens[0].symbol}`);
      setSelectedPublicToken(null);
      setSelectedTokenIndex(0);
    }
  }, [publicTokens, privateTokens, selectedPublicToken, selectedPrivateToken, setSelectedTokenIndex, setContextDirection]);

  const handleActionBarPortalClick = useCallback((direction: "in" | "out") => {
    if (!isConnected) {
      handleConnect();
      return;
    }
    // Prepare selection only after unlock succeeds — otherwise canceling unlock
    // leaves a token selected with no modal open.
    void privateUnlock.requireUnlock(() => {
      prepareActionBarPortalModal(direction);
      setShowActionBarPortalModal(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, prepareActionBarPortalModal, handleConnect, privateUnlock.requireUnlock]);

  const explorerBaseUrl = getExplorerBaseUrlForChain(chainId);

  const tokenPairs = useMemo(
    () => {
      const chainAddresses = getChainConfig(Number(chainId || "7082400"))?.addresses;
      return publicTokens.map(pub => {
        const priv = privateTokens.find(
          pt => pt.symbol === `p.${pub.symbol}` || pt.symbol === `p${pub.symbol}`,
        );
        return {
          symbol: pub.symbol,
          name: pub.name,
          icon: resolveTokenIcon(pub.symbol, pub.icon),
          publicBalance: pub.balance,
          privateBalance: priv?.balance || "0.00",
          privateSymbol: priv?.symbol || `p.${pub.symbol}`,
          publicAddress: pub.addressKey ? chainAddresses?.[pub.addressKey] : undefined,
          privateAddress: priv?.addressKey ? chainAddresses?.[priv.addressKey] : undefined,
        };
      });
    },
    [publicTokens, privateTokens, chainId],
  );

  // Scroll Synchronization
  const publicListRef = useRef<HTMLDivElement>(null);
  const privateListRef = useRef<HTMLDivElement>(null);
  const [activeScroller, setActiveScroller] = useState<'public' | 'private' | null>(null);

  const handleScroll = (source: 'public' | 'private') => {
    if (activeScroller && activeScroller !== source) return;

    const sourceRef = source === 'public' ? publicListRef : privateListRef;
    const targetRef = source === 'public' ? privateListRef : publicListRef;

    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollTop = sourceRef.current.scrollTop;
    }
  };

  const handleMouseEnter = (source: 'public' | 'private') => {
    setActiveScroller(source);
  };

  const handleMouseLeave = () => {
    setActiveScroller(null);
  };

  // ─── Welcome Screen (chain selector before connecting) ──────────────
  // Match WelcomeScreen: fall back to COTI testnet when env is unset, otherwise
  // `chainHexId !== undefined` is always true and every connect trips the switch gate.
  const envDefaultNetwork =
    import.meta.env.VITE_DEFAULT_NETWORK_ID || COTI_TESTNET_HEX;

  // Gate token card rendering during chain switch to prevent stale data flash.
  // A leftover pendingChainSwitch without a wagmi session must not spin forever.
  const [initialPendingChain] = useState(() => readPendingChainSwitch(hadPersistedSession));
  const [isSwitchingChain, setIsSwitchingChain] = useState(() => initialPendingChain !== null);
  const targetChainRef = useRef<string | null>(initialPendingChain);
  const pendingChainRef = useRef<string | null>(initialPendingChain);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSwitchGate = useCallback(() => {
    setIsSwitchingChain(false);
    targetChainRef.current = null;
    sessionStorage.removeItem('pendingChainSwitch');
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  // Reusable helper — resets every piece of local UI state to its initial values.
  const resetAllUiState = useCallback(() => {
    setSelectedPublicToken(null);
    setSelectedPrivateToken(null);
    setShowAmountModal(false);
    setShowSendModal(false);
    setShowReceiveModal(false);
    setShowActionBarPortalModal(false);
    setActionBarPortalDirection("in");
    setLocalDirection("to-private");
    privateUnlock.reset();
    console.log("[AES] resetAllUiState: clearing session key from memory");
    lockPrivateBalances?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockPrivateBalances, privateUnlock.reset]);

  const isWalletConnected = isConnected || wagmiConnected;
  const wasConnectedRef = useRef(isWalletConnected);
  const prevIsConnectedRef = useRef(isConnected);
  const [returnToWelcome, setReturnToWelcome] = useState(false);

  // Plugin session cleared while wagmi may still report connected — return to WelcomeScreen.
  useEffect(() => {
    if (prevIsConnectedRef.current && !isConnected) {
      setReturnToWelcome(true);
    }
    if (isConnected) {
      setReturnToWelcome(false);
      setRestoreTimedOut(false);
    }
    prevIsConnectedRef.current = isConnected;
  }, [isConnected]);

  // User reconnected via RainbowKit — leave welcome mode before wagmi→plugin sync completes.
  useEffect(() => {
    if (wagmiConnected) {
      setReturnToWelcome(false);
    }
  }, [wagmiConnected]);

  useEffect(() => {
    if (!isWalletConnected) {
      if (pendingChainRef.current || isSwitchingChain) {
        console.log('🔌 [WelcomeScreen] Ignoring transient disconnect during in-progress switch');
        return;
      }
      // Don't wipe session while the user is mid-unlock / onboarding.
      if (privateUnlock.isUnlocking) {
        console.log('🔌 [WelcomeScreen] Ignoring transient disconnect during unlock flow');
        return;
      }

      pendingChainRef.current = null;
      clearSwitchGate();

      if (wasConnectedRef.current) {
        console.log('🔌 [WelcomeScreen] Disconnected — clearing all session state');
        resetAllUiState();
      }
    }
    wasConnectedRef.current = isWalletConnected;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWalletConnected]);

  // Also gate when wallet is connected but tokens haven't loaded yet (initial render)
  const tokensNotReady = wagmiConnected && publicTokens.length === 0;

  // Detect external chain switch requests (e.g., RainbowKit auto-switch on reconnect).
  const [pendingExternalSwitch, setPendingExternalSwitch] = useState(false);
  const prevWagmiChainIdRef = useRef<number | undefined>(wagmiChainId);
  useEffect(() => {
    if (wagmiChainId && prevWagmiChainIdRef.current && wagmiChainId !== prevWagmiChainIdRef.current) {
      setPendingExternalSwitch(true);
    }
    prevWagmiChainIdRef.current = wagmiChainId;
  }, [wagmiChainId]);

  // Clear external switch gate when tokens match the new wagmiChainId
  useEffect(() => {
    if (pendingExternalSwitch && wagmiChainId && publicTokens.length > 0) {
      const firstToken = publicTokens[0];
      if (firstToken?.supportedChainIds?.includes(wagmiChainId)) {
        setPendingExternalSwitch(false);
      }
    }
  }, [pendingExternalSwitch, wagmiChainId, publicTokens]);

  const showWelcomeScreen = isSwitchingChain;

  // Log welcome screen state for debugging
  useEffect(() => {
    console.log('📺 [WelcomeScreen] state:', {
      showWelcomeScreen,
      isSwitchingChain,
      isConnected,
      wagmiConnected,
      wagmiStatus,
      isWagmiReconnecting,
      envDefaultNetwork,
      targetChain: targetChainRef.current,
      publicTokensCount: publicTokens.length,
      wagmiChainId,
    });
  }, [showWelcomeScreen, isSwitchingChain, isConnected, wagmiConnected, wagmiStatus, isWagmiReconnecting, publicTokens, wagmiChainId]);

  const { openConnectModal } = useConnectModal();
  const { switchChainAsync } = useSwitchChain();
  const { setInitialChainId } = useInitialChainSelector();

  // Clear the switching gate when wagmiChainId matches the target chain.
  useEffect(() => {
    if (isSwitchingChain && targetChainRef.current) {
      const targetDecimal = parseInt(targetChainRef.current, 16);
      if (wagmiChainId === targetDecimal) {
        console.log('🔗 [ChainSwitch] Gate clearing — wagmiChainId matches target:', { wagmiChainId, targetDecimal });
        clearSwitchGate();
      }
    }
  }, [isSwitchingChain, wagmiChainId, clearSwitchGate]);

  // After wallet connects, switch to the chain the user selected on the welcome screen.
  useEffect(() => {
    if ((isConnected || wagmiConnected) && pendingChainRef.current) {
      const targetChainHex = pendingChainRef.current;
      pendingChainRef.current = null;
      const targetChainId = parseInt(targetChainHex, 16);

      console.log('🔗 [ChainSwitch] Wallet connected, switching to target chain:', {
        targetChainHex,
        targetChainId,
        wagmiChainId,
      });

      if (wagmiChainId === targetChainId) {
        console.log('🔗 [ChainSwitch] Already on target chain — clearing gate');
        clearSwitchGate();
        return;
      }

      switchChainAsync({ chainId: targetChainId })
        .then(() => {
          console.log('🔗 [ChainSwitch] switchChain resolved — clearing gate');
          clearSwitchGate();
        })
        .catch((err) => {
          console.error('🔗 [ChainSwitch] switchChain error:', err);
          switchNetwork(targetChainHex);
          clearSwitchGate();
        });
    }
  }, [isConnected, wagmiConnected, wagmiChainId, switchChainAsync, switchNetwork, clearSwitchGate]);

  const handleChainSelect = useCallback((chainHexId: string) => {
    console.log('🎯 [ChainSelect] User clicked chain:', {
      chainHexId,
      envDefaultNetwork,
      openConnectModalAvailable: !!openConnectModal,
      isConnected,
      wagmiConnected,
    });

    setInitialChainId(parseInt(chainHexId, 16));

    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    pendingChainRef.current = chainHexId;
    setConnectInitiated(true);
    if (chainHexId !== envDefaultNetwork) {
      console.log('🎯 [ChainSelect] Setting gate (non-default chain)');
      setIsSwitchingChain(true);
      targetChainRef.current = chainHexId;
      sessionStorage.setItem('pendingChainSwitch', chainHexId);
      safetyTimeoutRef.current = setTimeout(() => {
        if (targetChainRef.current === chainHexId) {
          console.log('⏰ [ChainSelect] Safety timeout - clearing gate');
          clearSwitchGate();
        }
      }, 15000);
    } else {
      console.log('🎯 [ChainSelect] Default chain selected, no gate needed');
    }

    if (!openConnectModal) {
      console.error('❌ [ChainSelect] openConnectModal is undefined! RainbowKit modal not available');
      console.log('🔄 [ChainSelect] Attempting fallback with context handleConnect');
      handleConnect();
      return;
    }

    console.log('🎯 [ChainSelect] Opening RainbowKit connect modal...');
    openConnectModal();
    console.log('✅ [ChainSelect] openConnectModal() called');
  }, [openConnectModal, envDefaultNetwork, isConnected, wagmiConnected, handleConnect, clearSwitchGate, setInitialChainId]);

  // Persisted-session restore can hang (WalletConnect getProvider / leftover IndexedDB).
  // Drop the connection and return to welcome instead of spinning forever.
  useEffect(() => {
    const restoring =
      hadPersistedSession
      && !restoreTimedOut
      && (wagmiStatus === "reconnecting" || wagmiStatus === "connecting");
    if (!restoring || wagmiConnected) return;
    const timeoutId = window.setTimeout(() => {
      console.warn("⏰ [SessionRestore] Reconnect timed out — returning to welcome");
      setRestoreTimedOut(true);
      void disconnectAsync().catch(() => {});
      clearSwitchGate();
      setConnectInitiated(false);
      setReturnToWelcome(true);
    }, SESSION_RESTORE_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [hadPersistedSession, restoreTimedOut, wagmiStatus, wagmiConnected, disconnectAsync, clearSwitchGate]);


  // Show WelcomeScreen until the user connects a wallet, or after an explicit disconnect.
  // While wagmi is restoring a persisted session after refresh, keep the spinner — not welcome.
  // If there is no persisted session, skip the spinner even if status is `connecting`.
  if ((!isWalletConnected || returnToWelcome) && !isWagmiReconnecting) {
    return <WelcomeScreen onSelectChain={handleChainSelect} />;
  }

  // Post-connect / reconnect: spinner while the chain switch or session restore settles.
  // Hide network controls here — switching mid-reconnect races the session restore.
  if (showWelcomeScreen || isWagmiReconnecting) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar hideWalletButton hideNetworkSelector />
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <div
            role="status"
            aria-label="Connecting to network"
            className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"
          />
          <p className="text-foreground text-lg font-light tracking-wide">Connecting…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar hideNetworkSelector />

      {/* Background particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary/40"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, -120],
              x: [0, (Math.random() - 0.5) * 50],
              opacity: [0, 0.8, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeOut",
            }}
          />
        ))}
      </div>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pt-4 pb-12 relative z-10">
        <Tagline />

        {/* Main portal interface */}
        <div className="flex flex-col items-center justify-center gap-6 w-full max-w-6xl">
          <div className="relative w-full max-w-4xl flex flex-col gap-5">
            {/* Keep network control mounted during token reload so selection stays visible. */}
            <div className="flex w-full items-center justify-center overflow-x-auto px-1">
              <NetworkSelector
                variant="buttons"
                showLabel={false}
                activeChainId={
                  wagmiChainId != null
                    ? `0x${wagmiChainId.toString(16)}`
                    : chainId
                      ? (String(chainId).startsWith("0x")
                        ? String(chainId)
                        : `0x${Number(chainId).toString(16)}`)
                      : undefined
                }
                onSelectChain={(hexId) => {
                  const targetId = parseInt(hexId, 16);
                  if (wagmiChainId === targetId) return;
                  switchChainAsync({ chainId: targetId }).catch(() => {
                    switchNetwork(hexId);
                  });
                }}
              />
            </div>

            {(isSwitchingChain || tokensNotReady || pendingExternalSwitch) ? (
              <div role="status" aria-label="Loading tokens..." className="flex items-center justify-center w-full py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <Wallet isBlurred={false}>
                {/* USD Balance card */}
                <USDBalance
                  pairs={tokenPairs}
                  isPrivateUnlocked={privateUnlock.isUnlocked}
                  onToggleLock={() => void privateUnlock.toggleLock()}
                />

                {/* Action bar: Send / Receive / Portal In/Out */}
                <div className="w-full">
                  <ActionBar
                    isPortalActive={isBridgingLoading}
                    isPrivateUnlocked={privateUnlock.isUnlocked}
                    onPortalIn={() => handleActionBarPortalClick("in")}
                    onPortalOut={() => handleActionBarPortalClick("out")}
                    onSend={() => {
                      if (!isConnected) {
                        handleConnect();
                        return;
                      }
                      void privateUnlock.requireUnlock(() => setShowSendModal(true));
                    }}
                    onReceive={() => {
                      if (!isConnected) {
                        handleConnect();
                        return;
                      }
                      // Receive only shows the public address — no unlock needed.
                      setShowReceiveModal(true);
                    }}
                    onUnlock={() => void privateUnlock.toggleLock()}
                  />
                </div>

                {/* Combined Token Pairs — public + private in a single table */}
                <CombinedTokenCards
                  pairs={tokenPairs}
                  selectedToken={
                    showAmountModal || showActionBarPortalModal
                      ? null
                      : selectedPublicToken || selectedPrivateToken
                  }
                  selectedSide={
                    showAmountModal || showActionBarPortalModal
                      ? null
                      : selectedPublicToken
                        ? "public"
                        : selectedPrivateToken
                          ? "private"
                          : null
                  }
                  onSelectPublicToken={handleSelectPublicToken}
                  onSelectPrivateToken={handleSelectPrivateToken}
                  isPrivateUnlocked={privateUnlock.isUnlocked}
                  onShowUnlockModal={() => void privateUnlock.unlock()}
                  explorerBaseUrl={explorerBaseUrl}
                />
              </Wallet>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {/* Send Modal */}
      <SendModal
        isOpen={showSendModal && isPrivateUnlocked}
        isPrivateUnlocked={isPrivateUnlocked}
        onClose={() => setShowSendModal(false)}
        walletAddress={walletAddress}
        explorerBaseUrl={explorerBaseUrl}
        onSend={handleSendPrivateToken}
        privateTokens={privateTokens.map(token => {
          const chainIdNum = Number(chainId || "7082400");
          const tokenConfig = getPrivateTokensForChain(chainIdNum).find(t => t.symbol === token.symbol);
          const baseSymbol = token.symbol.replace(/^p\.?/, "");
          const publicToken = publicTokens.find(pt => pt.symbol === baseSymbol);
          const displayName = publicToken ? `Private ${publicToken.name}` : token.name;
          const chainAddresses = getChainConfig(chainIdNum)?.addresses;
          return {
            symbol: token.symbol,
            name: displayName,
            icon: resolveTokenIcon(token.symbol, token.icon),
            balance: token.balance,
            decimals: tokenConfig?.decimals ?? 18,
            contractAddress: token.addressKey
              ? chainAddresses?.[token.addressKey]
              : undefined,
          };
        })}
      />

      {/* Receive Modal */}
      <ReceiveModal
        isOpen={showReceiveModal}
        onClose={() => setShowReceiveModal(false)}
        walletAddress={walletAddress}
        explorerBaseUrl={explorerBaseUrl}
      />

      {/* Amount Input Modal (used for both token card clicks AND ActionBar Portal In/Out) */}
      <AmountInputModal
        isOpen={(showAmountModal || showActionBarPortalModal) && isPrivateUnlocked}
        isPrivateUnlocked={isPrivateUnlocked}
        onClose={() => {
          // Always clear token selection so the card isn't left highlighted after
          // ActionBar Portal In/Out (otherwise the next click only deselects).
          setShowActionBarPortalModal(false);
          handleCloseAmountModal();
        }}
        amount={amount}
        onAmountChange={setAmount}
        maxAmount={getSelectedTokenBalance()}
        tokenSymbol={selectedPublicToken || selectedPrivateToken || ""}
        direction={showActionBarPortalModal ? actionBarPortalDirection : (localDirection === "to-private" ? "in" : "out")}
        onConfirm={showActionBarPortalModal
          ? (actionBarPortalDirection === "in" ? handleConfirmPortalIn : handleConfirmPortalOut)
          : (localDirection === "to-private" ? handleConfirmPortalIn : handleConfirmPortalOut)
        }
        isProcessing={isBridgingLoading}
        isApprovalNeeded={isApprovalNeeded}
        onApprove={handleApprove}
        isApproving={isApproving}
        estimatedGasFee={podFeesMatchActiveChain ? estimatedGasFee : null}
        // Keep mismatch from forcing isGasEstimating — that blocked Max/slider at amount 0.
        isGasEstimating={isGasEstimating}
        portalFeeCoti={portalFeeCoti}
        portalFee={podFeesMatchActiveChain ? portalFee : null}
        portalFeeSymbol={effectivePortalFeeSymbol}
        podInboxFee={podFeesMatchActiveChain ? podInboxFee : null}
        l1GasFee={podFeesMatchActiveChain ? l1GasFee : null}
        isPodChain={effectiveIsPodChain}
        feeDebugInfo={feeDebugInfo}
        explorerBaseUrl={explorerBaseUrl}
        isBridgePaused={isBridgePaused}
        isDepositEnabled={isDepositEnabled}
        minDepositAmount={selectedBridge?.minDepositAmount}
        maxDepositAmount={selectedBridge?.maxDepositAmount}
        minWithdrawAmount={selectedBridge?.minWithdrawAmount}
        maxWithdrawAmount={selectedBridge?.maxWithdrawAmount}
        estimatedGasSymbol={indexPageUi.amountModalGasSymbol}
        fallbackCotiBalance={publicTokens.find((t) => t.symbol === "COTI")?.balance ?? null}
        connectedAddress={walletAddress}
        showPodRequestTracker={indexPageUi.showPodRequestTracker}
        podRequests={podRequests}
        podChainId={chainId ? Number(chainId) : undefined}
        tokenPairs={showActionBarPortalModal ? publicTokens.map(pub => {
          const priv = privateTokens.find(
            pt => pt.symbol === `p.${pub.symbol}` || pt.symbol === `p${pub.symbol}`
          );
          return {
            publicSymbol: pub.symbol,
            publicName: pub.name,
            privateSymbol: priv?.symbol || `p.${pub.symbol}`,
            icon: resolveTokenIcon(pub.symbol, pub.icon),
            publicBalance: pub.balance,
            privateBalance: priv?.balance || "0.00",
          };
        }) : undefined}
        onTokenSelect={showActionBarPortalModal ? (token) => {
          // Update selected token when user picks from dropdown
          const isGoingPrivate = actionBarPortalDirection === "in";
          if (isGoingPrivate) {
            setSelectedPublicToken(token.publicSymbol);
            setSelectedPrivateToken(null);
            const index = publicTokens.findIndex(t => t.symbol === token.publicSymbol);
            if (index !== -1) setSelectedTokenIndex(index);
          } else {
            setSelectedPrivateToken(token.privateSymbol);
            setSelectedPublicToken(null);
            const index = publicTokens.findIndex(t => t.symbol === token.publicSymbol);
            if (index !== -1) setSelectedTokenIndex(index);
          }
          setAmount("");
        } : undefined}
      />

      {/* Multiple Wallets Modal */}
      <MultipleWalletsModal
        isOpen={showMultipleWalletsModal}
        onClose={() => setShowMultipleWalletsModal(false)}
      />
    </div>
  );
};

export default Index;
